import React, { HTMLAttributes, useEffect, useMemo } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AutoComplete from '@mui/material/Autocomplete';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import { AddCircleOutlineOutlined, Edit as EditIcon } from '@mui/icons-material';
import { GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { InsightViewCellColorRuleItem } from 'types/api/SystemTenant/AROView/InsightViewDefinitionTypes';
import { CustomDataTable } from 'components/DatatableComponent';
import { useGetStandardViewCubeAttributesDropdown } from 'services/v1/SystemTenant/AROView/StandardViewDefinitionService';
import {
  isBetweenCondition,
  CONDITION_PHRASE,
} from 'pages/SystemTenant/AROView/InsightViewDefinition/components/ModalCellColorRule';
import { MAP_COLOR_CODE_WITH_COLOR_HEXA } from 'constant/ViewConstant';
import { ModalCellColorRuleEditor } from './ModalCellColorRuleEditor';
import {
  EffectiveCellColorItem,
  EffectiveCellColorRuleItem,
  useUpsertCellColorOverride,
  useDeleteCellColorOverride,
} from 'services/v1/Tenant/AROCube/CubeInsightViewCellColorService';

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 900,
  maxWidth: '95vw',
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: 1,
  py: 2,
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

const sectionBoxStyle = {
  bgcolor: '#FBFBFB',
  py: 1.5,
  px: 1.5,
  borderRadius: 1,
  border: '1px solid #E3EBF6',
};

const stepBadgeStyle = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  bgcolor: '#3B4797',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const SOURCE_CHIP: Record<string, { label: string; bgcolor: string; color: string }> = {
  SYSTEM: { label: 'System', bgcolor: '#EDF2FF', color: '#3B4797' },
  TENANT_OVERRIDE: { label: 'Override', bgcolor: 'rgba(66, 187, 147, 0.12)', color: '#1B7F45' },
  TENANT_DISABLED: { label: 'Disabled', bgcolor: '#F1F5F9', color: '#98A2AE' },
};

const renderOption = (props: HTMLAttributes<HTMLElement>, option: AutoCompleteItem) => {
  return (
    <Box component='li' sx={{ '& > span': { fontSize: '14px', mr: 1, flexShrink: 0 } }} {...props}>
      <span>{option.label}</span>
    </Box>
  );
};

const toEditorRule = (rule: EffectiveCellColorRuleItem): InsightViewCellColorRuleItem => ({
  id: rule.id,
  value: rule.value != null ? String(rule.value) : null,
  minValue: rule.minValue != null ? String(rule.minValue) : null,
  maxValue: rule.maxValue != null ? String(rule.maxValue) : null,
  includeMin: !!rule.includeMin,
  includeMax: !!rule.includeMax,
  priorityOrder: rule.priorityOrder,
  condition: rule.condition,
  color: rule.color,
  attributeReference: rule.attributeReference,
});

const toNumberOrNull = (value: string | null) => {
  if (value == null || value === '') return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const toRequestRule = (rule: InsightViewCellColorRuleItem, index: number): EffectiveCellColorRuleItem => {
  const between = isBetweenCondition(rule.condition);
  return {
    id: rule.id ?? `RULE-${crypto.randomUUID()}`,
    value: between ? null : toNumberOrNull(rule.value),
    minValue: between ? toNumberOrNull(rule.minValue) : null,
    maxValue: between ? toNumberOrNull(rule.maxValue) : null,
    includeMin: between ? !!rule.includeMin : null,
    includeMax: between ? !!rule.includeMax : null,
    priorityOrder: index,
    condition: rule.condition,
    color: rule.color,
    attributeReference: rule.attributeReference,
  };
};

// Plain-English rule summary, e.g. "If value above 100 → Green".
function RuleDescription(props: { rule: InsightViewCellColorRuleItem }) {
  const { rule } = props;
  if (!rule.condition || !rule.color) {
    return (
      <Typography variant='input-label-gray' fontStyle='italic'>
        Incomplete rule
      </Typography>
    );
  }
  const between = isBetweenCondition(rule.condition);
  const phrase = CONDITION_PHRASE[rule.condition.value as string] ?? rule.condition.label;
  const hexa = MAP_COLOR_CODE_WITH_COLOR_HEXA.get(rule.color.value.toString());
  const grayText = { color: '#828C99' };

  return (
    <Stack direction='row' alignItems='center' spacing={0.75} flexWrap='wrap' sx={{ py: 0.5 }}>
      <Typography variant='body2' sx={grayText}>
        If value
      </Typography>
      {between ? (
        <>
          <Typography variant='body2' sx={grayText}>
            between
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            {rule.minValue || '?'}
          </Typography>
          <Typography variant='body2' sx={grayText}>
            and
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            {rule.maxValue || '?'}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant='body2' sx={grayText}>
            {phrase}
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            {rule.attributeReference ? rule.attributeReference.label : rule.value || '?'}
          </Typography>
        </>
      )}
      <Typography variant='body2' sx={grayText}>
        →
      </Typography>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '3px',
          bgcolor: hexa ?? '#E0E0E0',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          flexShrink: 0,
        }}
      />
      <Typography variant='body2' fontWeight={600}>
        {rule.color.label}
      </Typography>
    </Stack>
  );
}

export interface ModalCellColorOverrideProps {
  visible: boolean;
  onClose: () => void;
  insightViewId?: string;
  cubeDefinitionId?: string;
  selectedData?: EffectiveCellColorItem;
  usedCubeAttributeIds: Set<string>;
  // When adding, preselect (and lock) the measure to the view's currently selected one.
  initialCubeAttributeId?: string;
}

export function ModalCellColorOverride(props: Readonly<ModalCellColorOverrideProps>) {
  const {
    visible,
    onClose,
    insightViewId,
    cubeDefinitionId,
    selectedData,
    usedCubeAttributeIds,
    initialCubeAttributeId,
  } = props;

  const [cubeAttribute, setCubeAttribute] = React.useState<AutoCompleteItem | null>(null);
  const [rules, setRules] = React.useState<InsightViewCellColorRuleItem[]>([]);
  const [ruleModalOpen, setRuleModalOpen] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState<InsightViewCellColorRuleItem | null>(null);

  const { data: cubeAttributeDropdown } = useGetStandardViewCubeAttributesDropdown(cubeDefinitionId);
  const { mutate: upsertOverride, isLoading: isSavingOverride } = useUpsertCellColorOverride(insightViewId);
  const { mutate: deleteOverride, isLoading: isDeletingOverride } = useDeleteCellColorOverride(insightViewId);

  const cubeDropdownOptions = useMemo<AutoCompleteItem[]>(() => {
    if (!cubeAttributeDropdown) return [];
    return cubeAttributeDropdown.data.filter((item) => !usedCubeAttributeIds.has(item.value.toString()));
  }, [cubeAttributeDropdown, usedCubeAttributeIds]);

  const handleOnCloseModal = () => {
    onClose();
    setCubeAttribute(null);
    setRules([]);
    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const handleOnSave = () => {
    if (!cubeAttribute) return;
    upsertOverride(
      {
        cubeAttributeId: cubeAttribute.value.toString(),
        body: {
          overrideState: 'OVERRIDE',
          rules: rules.map(toRequestRule),
        },
      },
      {
        onSuccess: () => handleOnCloseModal(),
        onError: () => {
          toast.error('Failed to save cell colour override');
        },
      },
    );
  };

  const handleOnDisable = () => {
    if (!cubeAttribute) return;
    upsertOverride(
      {
        cubeAttributeId: cubeAttribute.value.toString(),
        body: { overrideState: 'DISABLED' },
      },
      {
        onSuccess: () => handleOnCloseModal(),
        onError: () => {
          toast.error('Failed to disable cell colour');
        },
      },
    );
  };

  const handleOnResetToSystem = () => {
    if (!cubeAttribute) return;
    deleteOverride(
      { cubeAttributeId: cubeAttribute.value.toString() },
      {
        onSuccess: () => handleOnCloseModal(),
        onError: () => {
          toast.error('Failed to reset cell colour to system');
        },
      },
    );
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setRuleModalOpen(true);
  };

  const handleEditRule = (id: string) => {
    const rule = rules.find((r) => r.id === id);
    if (!rule) return;
    setEditingRule(rule);
    setRuleModalOpen(true);
  };

  const handleDeleteRule = (id: string) => {
    setRules((prev) => prev.filter((row) => row.id !== id));
  };

  const handleRuleModalSave = (incoming: InsightViewCellColorRuleItem) => {
    setRules((prev) => {
      if (incoming.id && prev.some((r) => r.id === incoming.id)) {
        return prev.map((r) => (r.id === incoming.id ? { ...incoming } : r));
      }
      const newId = incoming.id ?? `RULE-${crypto.randomUUID()}`;
      return [...prev, { ...incoming, id: newId, priorityOrder: prev.length }];
    });
    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const handleRuleModalClose = () => {
    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const usedAttributeRefs = useMemo(() => {
    const used = new Set<string>();
    if (cubeAttribute) used.add(cubeAttribute.value.toString());
    rules.forEach((row) => {
      if (row.attributeReference) used.add(row.attributeReference.value.toString());
    });
    return used;
  }, [cubeAttribute, rules]);

  const ruleAttributeReferenceOptions = useMemo<AutoCompleteItem[]>(() => {
    const currentRef = editingRule?.attributeReference;
    const allOptions = cubeAttributeDropdown?.data ?? [];
    return allOptions.filter((opt) => {
      if (currentRef && currentRef.value === opt.value) return true;
      return !usedAttributeRefs.has(opt.value.toString());
    });
  }, [cubeAttributeDropdown, usedAttributeRefs, editingRule]);

  const columns = [
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <IconButton onClick={() => handleEditRule(params.row.id)} size='small'>
            <EditIcon fontSize='small' />
          </IconButton>
          <IconButton onClick={() => handleDeleteRule(params.row.id)} size='small'>
            <HighlightOffRoundedIcon fontSize='small' />
          </IconButton>
        </Stack>
      ),
    },
    {
      field: 'priorityOrder',
      headerName: 'Priority',
      width: 90,
      sortable: false,
      renderCell: (params: any) => (
        <Box
          sx={{
            bgcolor: '#EDF1F7',
            color: '#3B4797',
            fontWeight: 600,
            px: 1.25,
            py: 0.25,
            borderRadius: 1,
            fontSize: 12,
          }}
        >
          P{params.value + 1}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Rule',
      flex: 1,
      minWidth: 460,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => <RuleDescription rule={params.row} />,
    },
    {
      field: 'attributeReference',
      headerName: 'Reference',
      width: 180,
      sortable: false,
      renderCell: (params: any) =>
        params.value ? (
          <Typography variant='input-label-gray' fontStyle='italic'>
            vs {params.value.label}
          </Typography>
        ) : (
          ''
        ),
    },
  ];

  const isValidForm =
    cubeAttribute &&
    rules.length > 0 &&
    rules.every((r) => {
      if (!r.condition || !r.color) return false;
      if (isBetweenCondition(r.condition)) {
        return r.minValue != null && r.minValue !== '' && r.maxValue != null && r.maxValue !== '';
      }
      return (r.value != null && r.value !== '') || r.attributeReference != null;
    });

  // In Add mode, lock the measure to the view's currently selected one.
  const measureLocked = !!selectedData || !!initialCubeAttributeId;

  // Resolve the preselected measure once the attribute dropdown has loaded.
  useEffect(() => {
    if (!visible || selectedData || !initialCubeAttributeId || cubeAttribute) return;
    const match = cubeAttributeDropdown?.data?.find(
      (item) => item.value?.toString() === initialCubeAttributeId,
    );
    if (match) setCubeAttribute(match);
  }, [visible, selectedData, initialCubeAttributeId, cubeAttributeDropdown, cubeAttribute]);

  useEffect(() => {
    if (!visible) return;
    if (selectedData) {
      setCubeAttribute(selectedData.cubeAttribute);
      setRules(selectedData.rules ? selectedData.rules.map((rule, idx) => ({ ...toEditorRule(rule), priorityOrder: idx })) : []);
    } else {
      setCubeAttribute(null);
      setRules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData, visible]);

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    const { oldIndex, targetIndex } = params;
    if (oldIndex === targetIndex) return;

    setRules((prevRules) => {
      const newRules = [...prevRules];
      const [movedRule] = newRules.splice(oldIndex, 1);
      newRules.splice(targetIndex, 0, movedRule);

      return newRules.map((rule, index) => ({
        ...rule,
        priorityOrder: index,
      }));
    });
  };

  const isMutating = isSavingOverride || isDeletingOverride;

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        <Stack direction='column' sx={ModalContentStyle} px={3} spacing={2}>
          {/* ── Header ── */}
          <Stack justifyContent='space-between' direction='row' alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={1.25}>
              <PaletteOutlinedIcon sx={{ color: '#42BB93' }} />
              <Stack spacing={0}>
                <Typography
                  variant='body1'
                  component='h2'
                  fontWeight='bold'
                  sx={{ color: '#3B4797', lineHeight: 1.2 }}
                >
                  Cell Colour
                </Typography>
                <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                  {selectedData
                    ? `Colour rules for ${selectedData.cubeAttribute?.label ?? 'this measure'} in this view`
                    : 'Add colour rules for a measure in this view'}
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={handleOnCloseModal} component='label'>
              <HighlightOffRoundedIcon sx={closeIconStyle} />
            </IconButton>
          </Stack>
          <Divider />

          {/* ── Purpose banner ── */}
          <Stack
            direction='row'
            spacing={1.25}
            sx={{
              p: 1.5,
              bgcolor: 'rgba(66, 187, 147, 0.06)',
              border: '1px solid rgba(66, 187, 147, 0.3)',
              borderRadius: 1,
            }}
          >
            <InfoOutlinedIcon fontSize='small' sx={{ color: '#42BB93', mt: '1px' }} />
            <Typography variant='body2' sx={{ color: '#475467' }}>
              Rules are checked from top to bottom; the first rule that matches a cell's value decides its colour.
              Drag rows to change the priority. Saving stores the rules as an override for your tenant only.
            </Typography>
          </Stack>

          {/* ── Step 1: Measure ── */}
          <Stack spacing={1.5} sx={sectionBoxStyle}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Box sx={stepBadgeStyle}>1</Box>
              <ViewInArOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
              <Stack spacing={0}>
                <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                  Measure
                </Typography>
                <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                  The measure these colour rules apply to
                </Typography>
              </Stack>
            </Stack>
            {measureLocked ? (
              <Stack direction='row' alignItems='center' spacing={1}>
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={0.75}
                  sx={{ px: 1.25, py: 0.75, bgcolor: '#EDF2FF', borderRadius: 1 }}
                >
                  <ViewInArOutlinedIcon sx={{ fontSize: 16, color: '#3B4797' }} />
                  <Typography variant='body2' sx={{ color: '#3B4797', fontWeight: 600 }}>
                    {selectedData?.cubeAttribute?.label ?? cubeAttribute?.label ?? 'Loading...'}
                  </Typography>
                </Stack>
                {selectedData &&
                  (() => {
                    const chip = SOURCE_CHIP[selectedData.source] ?? SOURCE_CHIP.SYSTEM;
                    return (
                      <Box
                        sx={{
                          bgcolor: chip.bgcolor,
                          color: chip.color,
                          fontWeight: 600,
                          px: 1,
                          py: 0.25,
                          borderRadius: 999,
                          fontSize: 11,
                        }}
                      >
                        {chip.label}
                      </Box>
                    );
                  })()}
                {!selectedData && (
                  <Typography variant='caption' sx={{ color: '#98A2AE', fontStyle: 'italic' }}>
                    from the selected measure
                  </Typography>
                )}
              </Stack>
            ) : (
              <Stack width='60%'>
                <AutoComplete
                  clearIcon={null}
                  size='small'
                  value={cubeAttribute}
                  onChange={(_, newValue) => setCubeAttribute(newValue)}
                  getOptionLabel={(option: AutoCompleteItem) => option.label}
                  isOptionEqualToValue={(option, value) => option.value?.toString() === value.value?.toString()}
                  options={cubeDropdownOptions}
                  renderOption={renderOption}
                  renderInput={(params) => (
                    <TextField {...params} placeholder='Select Measure' name='cubeAttribute' />
                  )}
                />
              </Stack>
            )}
          </Stack>

          {/* ── Step 2: Rules ── */}
          <Stack spacing={1.5} sx={sectionBoxStyle}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Box sx={stepBadgeStyle}>2</Box>
                <PaletteOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
                <Stack spacing={0}>
                  <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                    Conditional Rules
                  </Typography>
                  <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                    The first matching rule decides the cell colour
                  </Typography>
                </Stack>
              </Stack>
              <Button variant='main-table-panel' startIcon={<AddCircleOutlineOutlined />} onClick={handleAddRule}>
                Add Rule
              </Button>
            </Stack>

            <CustomDataTable
              rows={rules}
              columns={columns}
              checkboxSelection={false}
              disableSelectionOnClick
              sortingMode='client'
              filterMode='client'
              paginationMode='client'
              density='compact'
              rowHeight={45}
              autoHeight
              sx={{ backgroundColor: '#fff', minHeight: 260 }}
              getRowId={(row) => row.id}
              components={{ Toolbar: null, Pagination: null }}
              rowReordering
              onRowOrderChange={handleRowOrderChange}
            />
            <Typography variant='caption' sx={{ color: '#98A2AE' }}>
              {rules.length === 0
                ? 'Add at least one rule to save this cell colour.'
                : 'Drag rows to reorder. P1 is checked first.'}
            </Typography>
          </Stack>

          {selectedData?.source === 'SYSTEM' && (
            <Typography variant='caption' sx={{ color: '#98A2AE' }}>
              Saving creates an override for your tenant. The system definition itself is not modified.
            </Typography>
          )}

          <Divider />
          {/* ── Footer ── */}
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              {selectedData && selectedData.source !== 'TENANT_DISABLED' && (
                <Tooltip title='Turn off cell colours for this measure in this view'>
                  <span>
                    <ButtonLoading
                      onClick={handleOnDisable}
                      loading={isSavingOverride}
                      disabled={isMutating}
                      variant='outlined'
                      sx={{ textTransform: 'none' }}
                      color='error'
                    >
                      Disable Colours
                    </ButtonLoading>
                  </span>
                </Tooltip>
              )}
              {selectedData?.overrideId && (
                <Tooltip title='Remove your override and return to the system rules'>
                  <span>
                    <ButtonLoading
                      onClick={handleOnResetToSystem}
                      loading={isDeletingOverride}
                      disabled={isMutating}
                      variant='outlined'
                      sx={{ textTransform: 'none' }}
                    >
                      Reset to System
                    </ButtonLoading>
                  </span>
                </Tooltip>
              )}
            </Stack>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Button variant='main-table-panel-border' onClick={handleOnCloseModal} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <ButtonLoading
                variant='main-table-panel'
                disabled={!isValidForm || isMutating}
                loading={isSavingOverride}
                onClick={handleOnSave}
                sx={{ textTransform: 'none' }}
              >
                Save Cell Colour
              </ButtonLoading>
            </Stack>
          </Stack>
        </Stack>
      </Modal>

      <ModalCellColorRuleEditor
        visible={ruleModalOpen}
        onClose={handleRuleModalClose}
        onSave={handleRuleModalSave}
        initialData={editingRule}
        attributeReferenceOptions={ruleAttributeReferenceOptions}
      />
    </div>
  );
}
